const API_BASE_URL = import.meta.env.VITE_API_URL ?? "http://localhost:3000";

interface ApiOptions {
  method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  body?: unknown;
  headers?: Record<string, string>;
}

interface ApiError {
  error: string;
  status: number;
}

let accessToken: string | null = null;
let sessionRefreshToken: string | null = null;
let refreshPromise: Promise<string> | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
  if (!token) sessionRefreshToken = null;
}

export async function apiFetch<T>(
  path: string,
  options: ApiOptions = {}
): Promise<T> {
  return apiFetchInternal<T>(path, options, true);
}

async function apiFetchInternal<T>(
  path: string,
  options: ApiOptions,
  allowRefresh: boolean,
): Promise<T> {
  const { method = "GET", body, headers = {} } = options;

  const requestHeaders: Record<string, string> = {
    "Content-Type": "application/json",
    ...headers,
  };

  if (accessToken) {
    requestHeaders["Authorization"] = `Bearer ${accessToken}`;
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers: requestHeaders,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (
    allowRefresh &&
    response.status === 401 &&
    sessionRefreshToken &&
    !path.startsWith("/api/auth/")
  ) {
    refreshPromise ??= refreshAccessToken(sessionRefreshToken).finally(() => {
      refreshPromise = null;
    });
    await refreshPromise;
    return apiFetchInternal<T>(path, options, false);
  }

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({
      error: "Request failed",
    })) as ApiError;
    throw new ApiRequestError(
      errorBody.error ?? "Request failed",
      response.status
    );
  }

  return response.json() as Promise<T>;
}

export class ApiRequestError extends Error {
  constructor(
    message: string,
    public readonly status: number
  ) {
    super(message);
    this.name = "ApiRequestError";
  }
}

export async function authenticateWithTelegram(
  initData: string
): Promise<{ accessToken: string; refreshToken: string }> {
  const result = await apiFetch<{
    accessToken: string;
    refreshToken: string;
  }>("/api/auth/telegram", {
    method: "POST",
    body: { initData },
  });
  setAccessToken(result.accessToken);
  sessionRefreshToken = result.refreshToken;
  return result;
}

export async function refreshAccessToken(
  refreshToken: string
): Promise<string> {
  const result = await apiFetch<{ accessToken: string }>(
    "/api/auth/refresh",
    {
      method: "POST",
      body: { refreshToken },
    }
  );
  setAccessToken(result.accessToken);
  return result.accessToken;
}
